import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  FlatList,
  ActivityIndicator,
  RefreshControl,
  ScrollView,
} from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import Animated, { FadeInDown } from 'react-native-reanimated';
import {
  Bug,
  Lightbulb,
  Palette,
  HelpCircle,
  ChevronRight,
  ShieldX,
  Smartphone,
  Globe,
  MessageSquare,
} from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { useAdminRole, useAdminFeedback, hasPermission } from '@/lib/admin';
import type { AdminFeedbackItem, FeedbackStatus } from '@/lib/admin';
import type { FeedbackCategory } from '@/lib/feedback/types';
import { hapticLight, hapticSelection } from '@/lib/haptics';
import { AppLayout } from '@/components/AppLayout';
import { WebCard } from '@/components/web/WebAppShell';
import { FeedbackStatusBadge } from '@/components/admin/FeedbackStatusBadge';
import { FeedbackPriorityBadge } from '@/components/admin/FeedbackPriorityBadge';
import { isWeb } from '@/lib/platform';

type FilterCategory = FeedbackCategory | 'all';
type FilterStatus = FeedbackStatus | 'all';

const CATEGORIES: { key: FilterCategory; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'bug', label: 'Bug' },
  { key: 'feature', label: 'Feature' },
  { key: 'ux', label: 'UX' },
  { key: 'other', label: 'Other' },
];

const STATUS_FILTERS: { key: FilterStatus; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'in_review', label: 'In Review' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'closed', label: 'Closed' },
];

const CATEGORY_ICON: Record<FeedbackCategory, typeof Bug> = {
  bug: Bug,
  feature: Lightbulb,
  ux: Palette,
  other: HelpCircle,
};

const CATEGORY_COLOR: Record<FeedbackCategory, string> = {
  bug: '#FF3B30',
  feature: '#007AFF',
  ux: '#FF9500',
  other: '#8E8E93',
};

export default function AdminFeedbackScreen() {
  const colors = useThemeColors();
  const router = useRouter();
  const [categoryFilter, setCategoryFilter] = useState<FilterCategory>('all');
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('all');

  const { data: adminRole, isLoading: roleLoading } = useAdminRole();
  const {
    data: feedback,
    isLoading,
    refetch,
    isRefetching,
  } = useAdminFeedback({
    category: categoryFilter === 'all' ? undefined : categoryFilter,
    status: statusFilter === 'all' ? undefined : statusFilter,
  });

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleItemPress = (item: AdminFeedbackItem) => {
    hapticLight();
    router.push(`/(admin)/feedback-detail?id=${item.id}`);
  };

  const renderItem = useCallback(
    ({ item, index }: { item: AdminFeedbackItem; index: number }) => {
      const Icon = CATEGORY_ICON[item.category];
      const categoryColor = CATEGORY_COLOR[item.category];

      return (
        <Animated.View entering={FadeInDown.delay(index * 50).duration(400)}>
          <AnimatedPressable
            onPress={() => handleItemPress(item)}
            className="mb-3"
          >
            <WebCard>
              {/* Top row: category + status + priority */}
              <View className="flex-row items-center justify-between mb-2">
                <View className="flex-row items-center gap-2">
                  <View
                    className="px-2.5 py-1 rounded-lg flex-row items-center gap-1.5"
                    style={{ backgroundColor: `${categoryColor}15` }}
                  >
                    <Icon size={13} color={categoryColor} />
                    <Text style={{ color: categoryColor }} className="text-xs font-bold uppercase">
                      {item.category}
                    </Text>
                  </View>
                  <FeedbackStatusBadge status={item.status} />
                  <FeedbackPriorityBadge priority={item.priority} />
                </View>
                <ChevronRight size={16} color={colors.text.tertiary} />
              </View>

              {/* Text preview */}
              <Text
                style={{ color: colors.text.primary }}
                className="text-sm leading-relaxed"
                numberOfLines={2}
              >
                {item.text}
              </Text>

              {/* Meta row */}
              <View className="flex-row items-center gap-3 mt-2.5">
                <Text style={{ color: colors.text.tertiary }} className="text-xs">
                  {item.user_email}
                </Text>
                <View className="w-1 h-1 rounded-full" style={{ backgroundColor: colors.border.medium }} />
                <View className="flex-row items-center gap-1">
                  {item.platform === 'web' ? (
                    <Globe size={11} color={colors.text.tertiary} />
                  ) : (
                    <Smartphone size={11} color={colors.text.tertiary} />
                  )}
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">
                    {item.platform}
                  </Text>
                </View>
                <View className="w-1 h-1 rounded-full" style={{ backgroundColor: colors.border.medium }} />
                <Text style={{ color: colors.text.tertiary }} className="text-xs">
                  {formatDate(item.created_at)}
                </Text>
              </View>
            </WebCard>
          </AnimatedPressable>
        </Animated.View>
      );
    },
    [colors]
  );

  // Permission gate (after all hooks)
  if (!roleLoading && !hasPermission(adminRole, 'feedback')) {
    return (
      <AppLayout
        title="Feedback"
        breadcrumbs={[
          { label: 'Admin', href: '/(admin)/admin-dashboard' },
          { label: 'Feedback' },
        ]}
      >
        <View className="flex-1 items-center justify-center px-8" style={{ backgroundColor: colors.surface.secondary }}>
          <ShieldX size={48} color={colors.text.tertiary} />
          <Text style={{ color: colors.text.primary }} className="text-xl font-bold mt-4 text-center">
            Access Restricted
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-sm text-center mt-2">
            Your admin role does not have permission to view feedback.
          </Text>
        </View>
      </AppLayout>
    );
  }

  if (!isWeb) {
    return (
      <MobileFeedbackScreen
        feedback={feedback}
        isLoading={isLoading}
        isRefetching={isRefetching}
        refetch={refetch}
        categoryFilter={categoryFilter}
        setCategoryFilter={setCategoryFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        onItemPress={handleItemPress}
        formatDate={formatDate}
        colors={colors}
      />
    );
  }

  return (
    <AppLayout
      title="Feedback"
      breadcrumbs={[
        { label: 'Admin', href: '/(admin)/admin-dashboard' },
        { label: 'Feedback' },
      ]}
    >
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.surface.secondary }} edges={['bottom']}>
        {/* Header + count */}
        <View className="px-1 pt-2 pb-3">
          <Text style={{ color: colors.text.primary }} className="text-xl font-bold">
            Feedback
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-sm mt-1">
            {feedback?.length ?? 0} submissions
          </Text>
        </View>

        {/* Status filter pills */}
        <View style={{ paddingBottom: 10 }}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={{ flexGrow: 0 }}
            contentContainerStyle={{ paddingHorizontal: 4, gap: 8 }}
          >
            {STATUS_FILTERS.map((sf) => {
              const active = statusFilter === sf.key;
              return (
                <AnimatedPressable
                  key={sf.key}
                  onPress={() => {
                    hapticSelection();
                    setStatusFilter(sf.key);
                  }}
                  className="px-4 rounded-full items-center justify-center"
                  style={{
                    backgroundColor: active ? colors.primary[600] : colors.surface.primary,
                    height: 32,
                  }}
                >
                  <Text
                    style={{ color: active ? '#FFFFFF' : colors.text.secondary }}
                    className="text-xs font-semibold"
                  >
                    {sf.label}
                  </Text>
                </AnimatedPressable>
              );
            })}
          </ScrollView>
        </View>

        {/* Category filter pills */}
        <View style={{ paddingBottom: 10 }}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={{ flexGrow: 0 }}
            contentContainerStyle={{ paddingHorizontal: 4, gap: 8 }}
          >
            {CATEGORIES.map((cat) => {
              const active = categoryFilter === cat.key;
              return (
                <AnimatedPressable
                  key={cat.key}
                  onPress={() => {
                    hapticSelection();
                    setCategoryFilter(cat.key);
                  }}
                  className="px-4 rounded-full items-center justify-center"
                  style={{
                    backgroundColor: active ? colors.surface.tertiary : colors.surface.primary,
                    borderWidth: 1,
                    borderColor: active ? colors.border.medium : colors.border.light,
                    height: 32,
                  }}
                >
                  <Text
                    style={{ color: active ? colors.text.primary : colors.text.secondary }}
                    className="text-xs font-semibold"
                  >
                    {cat.label}
                  </Text>
                </AnimatedPressable>
              );
            })}
          </ScrollView>
        </View>

        {/* Divider */}
        <View style={{ height: 1, backgroundColor: colors.border.light, marginBottom: 8 }} />

        {/* List */}
        {isLoading ? (
          <View className="flex-1 items-center justify-center">
            <ActivityIndicator size="large" color={colors.primary[600]} />
          </View>
        ) : !feedback?.length ? (
          <View className="flex-1 items-center justify-center px-8">
            <MessageSquare size={48} color={colors.text.tertiary} />
            <Text style={{ color: colors.text.secondary }} className="text-base font-medium mt-4 text-center">
              No feedback found
            </Text>
          </View>
        ) : (
          <FlatList
            data={feedback}
            keyExtractor={(item) => item.id}
            renderItem={renderItem}
            contentContainerStyle={{ paddingTop: 4, paddingBottom: 24 }}
            refreshControl={
              <RefreshControl
                refreshing={isRefetching}
                onRefresh={refetch}
                tintColor={colors.primary[600]}
              />
            }
          />
        )}
      </SafeAreaView>
    </AppLayout>
  );
}

// ─── Mobile Layout ────────────────────────────────────────────────────────────

interface MobileFeedbackScreenProps {
  feedback: AdminFeedbackItem[] | undefined;
  isLoading: boolean;
  isRefetching: boolean;
  refetch: () => void;
  categoryFilter: FilterCategory;
  setCategoryFilter: (v: FilterCategory) => void;
  statusFilter: FilterStatus;
  setStatusFilter: (v: FilterStatus) => void;
  onItemPress: (item: AdminFeedbackItem) => void;
  formatDate: (iso: string) => string;
  colors: ReturnType<typeof useThemeColors>;
}

function MobileFeedbackScreen({
  feedback,
  isLoading,
  isRefetching,
  refetch,
  categoryFilter,
  setCategoryFilter,
  statusFilter,
  setStatusFilter,
  onItemPress,
  formatDate,
  colors,
}: MobileFeedbackScreenProps) {
  const renderItem = useCallback(
    ({ item, index }: { item: AdminFeedbackItem; index: number }) => {
      const Icon = CATEGORY_ICON[item.category];
      const categoryColor = CATEGORY_COLOR[item.category];

      return (
        <Animated.View entering={FadeInDown.delay(index * 50).duration(400)}>
          <AnimatedPressable
            onPress={() => onItemPress(item)}
            className="mb-2"
          >
            <View
              className="px-3 py-2.5 rounded-xl"
              style={{
                backgroundColor: colors.surface.primary,
                borderWidth: 1,
                borderColor: colors.border.light,
              }}
            >
              {/* Top row: category + status + priority + chevron */}
              <View className="flex-row items-center justify-between mb-1.5">
                <View className="flex-row items-center gap-1.5 flex-wrap flex-1 mr-2">
                  {/* Category badge */}
                  <View
                    className="px-2 py-0.5 rounded-md flex-row items-center gap-1"
                    style={{ backgroundColor: `${categoryColor}15` }}
                  >
                    <Icon size={11} color={categoryColor} />
                    <Text style={{ color: categoryColor }} className="text-xs font-bold uppercase" numberOfLines={1}>
                      {item.category}
                    </Text>
                  </View>
                  <FeedbackStatusBadge status={item.status} size="sm" />
                  <FeedbackPriorityBadge priority={item.priority} size="sm" />
                </View>
                <ChevronRight size={14} color={colors.text.tertiary} />
              </View>

              {/* Text preview */}
              <Text
                style={{ color: colors.text.primary }}
                className="text-sm leading-relaxed"
                numberOfLines={2}
              >
                {item.text}
              </Text>

              {/* Meta: email on line 1, platform + date on line 2 */}
              <View className="mt-1.5 gap-0.5">
                <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>
                  {item.user_email}
                </Text>
                <View className="flex-row items-center gap-1">
                  {item.platform === 'web' ? (
                    <Globe size={10} color={colors.text.tertiary} />
                  ) : (
                    <Smartphone size={10} color={colors.text.tertiary} />
                  )}
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">
                    {item.platform}
                  </Text>
                  <View className="w-0.5 h-0.5 rounded-full mx-0.5" style={{ backgroundColor: colors.border.medium }} />
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">
                    {formatDate(item.created_at)}
                  </Text>
                </View>
              </View>
            </View>
          </AnimatedPressable>
        </Animated.View>
      );
    },
    [colors, onItemPress, formatDate]
  );

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.surface.secondary }} edges={['top', 'bottom']}>
      {/* Header */}
      <View className="px-4 pt-4 pb-2">
        <Text style={{ color: colors.text.primary }} className="text-xl font-bold">
          Feedback
        </Text>
        <Text style={{ color: colors.text.secondary }} className="text-sm mt-0.5">
          {feedback?.length ?? 0} submissions
        </Text>
      </View>

      {/* Status filter pills */}
      <View style={{ paddingBottom: 8 }}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{ flexGrow: 0 }}
          contentContainerStyle={{ paddingHorizontal: 4, gap: 6 }}
        >
          {STATUS_FILTERS.map((sf) => {
            const active = statusFilter === sf.key;
            return (
              <AnimatedPressable
                key={sf.key}
                onPress={() => {
                  hapticSelection();
                  setStatusFilter(sf.key);
                }}
                className="px-3 rounded-full items-center justify-center"
                style={{
                  backgroundColor: active ? colors.primary[600] : colors.surface.primary,
                  height: 32,
                }}
              >
                <Text
                  style={{ color: active ? '#FFFFFF' : colors.text.secondary }}
                  className="text-xs font-semibold"
                  numberOfLines={1}
                >
                  {sf.label}
                </Text>
              </AnimatedPressable>
            );
          })}
        </ScrollView>
      </View>

      {/* Category filter pills */}
      <View style={{ paddingBottom: 8 }}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{ flexGrow: 0 }}
          contentContainerStyle={{ paddingHorizontal: 4, gap: 6 }}
        >
          {CATEGORIES.map((cat) => {
            const active = categoryFilter === cat.key;
            return (
              <AnimatedPressable
                key={cat.key}
                onPress={() => {
                  hapticSelection();
                  setCategoryFilter(cat.key);
                }}
                className="px-3 rounded-full items-center justify-center"
                style={{
                  backgroundColor: active ? colors.surface.tertiary : colors.surface.primary,
                  borderWidth: 1,
                  borderColor: active ? colors.border.medium : colors.border.light,
                  height: 32,
                }}
              >
                <Text
                  style={{ color: active ? colors.text.primary : colors.text.secondary }}
                  className="text-xs font-semibold"
                  numberOfLines={1}
                >
                  {cat.label}
                </Text>
              </AnimatedPressable>
            );
          })}
        </ScrollView>
      </View>

      {/* Divider */}
      <View style={{ height: 1, backgroundColor: colors.border.light, marginBottom: 6 }} />

      {/* List */}
      {isLoading ? (
        <View className="flex-1 items-center justify-center">
          <ActivityIndicator size="large" color={colors.primary[600]} />
        </View>
      ) : !feedback?.length ? (
        <View className="flex-1 items-center justify-center px-8">
          <MessageSquare size={40} color={colors.text.tertiary} />
          <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mt-3 text-center">
            No feedback found
          </Text>
        </View>
      ) : (
        <FlatList
          data={feedback}
          keyExtractor={(item) => item.id}
          renderItem={renderItem}
          contentContainerStyle={{ paddingTop: 2, paddingBottom: 24, paddingHorizontal: 0 }}
          refreshControl={
            <RefreshControl
              refreshing={isRefetching}
              onRefresh={refetch}
              tintColor={colors.primary[600]}
            />
          }
        />
      )}
    </SafeAreaView>
  );
}
